/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { irrPlayback } from '../../call/+state/call.actions';
import { selectIrrVolume } from '../../call/+state/media.selectors';

@Injectable({
    providedIn: 'root'
})
export class IrrService {
    private readonly audioContext: AudioContext;
    private readonly mixerNode: GainNode;
    private mediaElementAudioSourceNodeMap: { [id:string]: MediaElementAudioSourceNode } = {};
    private readonly mediaStreamAudioDestinationNode: MediaStreamAudioDestinationNode;
    private gain = 1;
    public readonly mediaStream: MediaStream;

    constructor(private store: Store) {
        console.debug(`Creating irr audio context`);
        this.audioContext = new AudioContext();
        this.mixerNode = this.audioContext.createGain();
        this.mediaStreamAudioDestinationNode = this.audioContext.createMediaStreamDestination();
        this.mixerNode.connect(this.mediaStreamAudioDestinationNode);
        this.mediaStream = this.mediaStreamAudioDestinationNode.stream;
        console.debug(`Acquired irr media stream: ${this.mediaStream.id}`);
        this.store.select(selectIrrVolume).subscribe((volume) => this.setVolume(volume));
    }

    public addIrrMediaElementSource(audio: HTMLAudioElement) {
        console.debug(`Routing irr audio for ${audio.src} to media stream ${this.mediaStream.id}`);
        this.mediaElementAudioSourceNodeMap[audio.id] = new MediaElementAudioSourceNode(this.audioContext, { mediaElement: audio });
        this.mediaElementAudioSourceNodeMap[audio.id].connect(this.mixerNode);
        audio.onplaying = () => this.store.dispatch(irrPlayback( { isPlaying: true } ));
        audio.onpause = () => this.store.dispatch(irrPlayback( { isPlaying: false } ));
        this.checkContext();
    }

    public removeIrrMediaElementSource(audio: HTMLAudioElement) {
        console.debug(`Removing irr audio for ${audio.src} from media stream ${this.mediaStream.id}`);
        this.mediaElementAudioSourceNodeMap[audio.id]?.disconnect(this.mixerNode);
        delete this.mediaElementAudioSourceNodeMap[audio.id];
        this.checkContext();
    }

    public checkContext() {
        if (this.audioContext.state === 'suspended') {
            this.audioContext
                .resume()
                .then(() => console.debug('Successfully started irr audio context'))
                .catch((err) => console.error('Unable to start irr audio context {}', err));
        }
    }

    /* volume: 1-10 which is converted to a gain of 0.1-2.0 by dividing by 5 */
    public setVolume(volume: number) {
        this.gain = volume / 5;
        console.debug(`Setting gain to ${this.gain} for IRR playback`);
        if (this.mixerNode) this.mixerNode.gain.value = this.gain;
    }
}
